'use client';

import { useEffect } from 'react';

type TrackingQueue = ((...args: unknown[]) => void) & { q: unknown[][] };
type TrustedScriptUrlLike = { toString(): string };
type TrustedTrackingPolicy = {
  createScriptURL: (value: string) => TrustedScriptUrlLike;
};
type TrustedTypesApi = {
  createPolicy: (
    name: string,
    rules: { createScriptURL: (value: string) => string }
  ) => TrustedTrackingPolicy;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
    clarity?: TrackingQueue;
    lintrk?: TrackingQueue;
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    __zapiTrackingReady?: boolean;
    __zapiTrackingScriptPolicy?: TrustedTrackingPolicy | null;
    trustedTypes?: TrustedTypesApi;
  }
}

// A long delay makes short visits disappear from Analytics and creates a false
// traffic drop during migration comparisons. A brief delay still keeps tags
// outside the critical rendering path without sacrificing most page views.
const ACTIVATION_DELAY_MS = 3_000;

const TRACKING_SCRIPT_HOSTS = new Set([
  'www.googletagmanager.com',
  'www.clarity.ms',
  'snap.licdn.com',
  'js.hs-scripts.com',
]);

function trustedTrackingScriptUrl(src: string): TrustedScriptUrlLike | string {
  if (!window.trustedTypes) return src;

  const policy =
    window.__zapiTrackingScriptPolicy ??
    (window.__zapiTrackingScriptPolicy = window.trustedTypes.createPolicy('zapi-tracking', {
      createScriptURL: (value) => {
        const url = new URL(value, window.location.origin);

        if (url.protocol !== 'https:' || !TRACKING_SCRIPT_HOSTS.has(url.hostname)) {
          throw new TypeError(`Untrusted tracking script URL: ${value}`);
        }

        return url.toString();
      },
    }));

  return policy.createScriptURL(src);
}

function appendScript(id: string, src: string) {
  if (document.getElementById(id)) return;

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  const nonce = document.querySelector('script[nonce]')?.getAttribute('nonce');
  if (nonce) script.nonce = nonce;
  script.src = trustedTrackingScriptUrl(src) as string;
  script.referrerPolicy = 'strict-origin-when-cross-origin';
  document.head.appendChild(script);
}

function createQueue(): TrackingQueue {
  const queue = ((...args: unknown[]) => {
    queue.q.push(args);
  }) as TrackingQueue;
  queue.q = [];
  return queue;
}

function initialiseTracking() {
  if (window.__zapiTrackingReady) return;

  window.__zapiTrackingReady = true;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  appendScript('google-tag-manager-5wtfql2', 'https://www.googletagmanager.com/gtm.js?id=GTM-5WTFQL2');

  // Meta Pixel is managed by the GTM container. Loading fbevents.js here too
  // created duplicate pixels and a conflicting-version warning in production.

  window.clarity = window.clarity ?? createQueue();
  appendScript('clarity-u4yeqoy681', 'https://www.clarity.ms/tag/u4yeqoy681');
  appendScript('clarity-x7y0x86ekw', 'https://www.clarity.ms/tag/x7y0x86ekw');

  window._linkedin_partner_id = '1893674';
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? [];
  if (!window._linkedin_data_partner_ids.includes(window._linkedin_partner_id)) {
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
  }
  window.lintrk = window.lintrk ?? createQueue();
  appendScript('linkedin-insight', 'https://snap.licdn.com/li.lms-analytics/insight.min.js');

  appendScript('hubspot', 'https://js.hs-scripts.com/50628289.js');
  window.dispatchEvent(new Event('zapi:tracking-ready'));
}

/**
 * Keeps third-party marketing code outside the critical rendering path. It
 * starts after a visitor action or an extended dwell time, preserving tracking
 * while keeping the first load responsive on constrained mobile devices.
 */
export default function DeferredTracking() {
  useEffect(() => {
    let timer = window.setTimeout(initialiseTracking, ACTIVATION_DELAY_MS);

    const activate = () => {
      window.clearTimeout(timer);
      timer = 0;
      initialiseTracking();
    };

    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    events.forEach((event) => window.addEventListener(event, activate, { once: true, passive: true }));

    return () => {
      if (timer) window.clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, activate));
    };
  }, []);

  return null;
}
